import AbstractMethodError from '../../error/AbstractMethodError.js';
import FACTORY from '../FACTORY/FACTORY.js';
import MissingContainerError from '../../error/MissingContainerError.js';
import PAYLOAD from '../DOM/form/PAYLOAD.js';
import RecursionLimitError from '../../error/RecursionLimitError.js';
import { STATUS } from '../../enums/STATUS.js';
import { HTMLELEMENT } from '../../enums/HTMLELEMENT.js';
/** A Generic HTML Element Class */
export default class EL extends FACTORY {
    /** The parent element of this element */
    node;
    /** Element Class name */
    className;
    /** HTML Element Tag (ie: 'DIV', 'SPAN') */
    element;
    /** The HTML Elemeent Object */
    el;
    /** State Indicator */
    status;
    /** Represents the location where children of this element are instantiated */
    surrogate;
    /** A collection of child elements */
    children;
    /** A collection of public Methods */
    methods;
    events;
    handlers;
    callers;
    /** Constructs a node representing an HTMLElement that can be represented in the DOM
        @param node Parent Node
        @param element HTMLElement tagName
        @param model Model
    */
    constructor(node, element, model) {
        super(model);
        this.node = node;
        this.className = this.constructor.name;
        this.element = element;
        this.status = STATUS.DEFAULT;
        this.surrogate = this;
        this.children = new Array();
        this.dialog = null;
        this.methods = {};
        this.events = {};
        this.handlers = {};
        this.callers = new Array();
        this.el = this.makeElement(model);
        this.merge(model);
    }
    /** Creates the DOM HTMLElement during instantiation
     * Override this method to create a custom element
     */
    createElement() {
        const el = document.createElement(this.element);
        return el;
    }
    /** Creates an HTMLElement based based on this MODEL and appends to this Node Element */
    makeElement(model) {
        const el = this.createElement();
        if (this.node instanceof EL) {
            this.node.el.appendChild(el);
        }
        else {
            this.node.appendChild(el);
        }
        return el;
    }
    /** If no children supplied, resolve this Promise */
    ifEmpty() {
        return Promise.resolve(this);
    }
    addClassAttribute(className) {
        if (this.attributes && typeof this.attributes['class'] === 'string') {
            if (!this.attributes['class'].split(' ').includes(className)) {
                this.attributes['class'] += ' ' + className;
            }
        }
        else if (this.attributes) {
            this.attributes['class'] = className;
        }
        return this;
    }
    /** Adds an array of classes */
    addClassInline(classes) {
        classes.forEach((c) => {
            try {
                if (c.length > 0) {
                    this.el.classList.add(c);
                    this.addClassAttribute(c);
                }
            }
            catch (e) {
                console.warn(this.toString() + ' Unable to add class to classList', [c, classes], e);
            }
        });
    }
    /** Adds the given class name to the element's list of classes
        @param className the class to be appended
        @param conditional Optionally perform method on true
    */
    addClass(className, conditional = true) {
        if (conditional) {
            try {
                if (typeof className === 'string') {
                    this.addClassInline(className.split(' '));
                }
                else if (Array.isArray(className)) {
                    this.addClassInline(className);
                }
            }
            catch (e) {
                console.error('Unable to add classname', className, typeof className, e);
            }
        }
        return this;
    }
    /** Logs a debug message to the console if debug mode is enabled
     * @param args The debug message(s) and any additional parameters
     * @return this
    */
    logDebug(...args) {
        const main = this.getMain();
        if (main && main.getConfig().app.console.debug) {
            const stack = new Error().stack || '';
            const stackLines = stack.split('\n');
            // stackLines[1] is this debug method, stackLines[2] is the actual caller
            const callerInfo = stackLines[2]?.trim() || 'unknown';
            console.log('%c[DEBUG]', 'color: orange;', `${this.toString()}: ${callerInfo}`, ...args);
        }
        return this;
    }
    /** Logs an info message to the console if debug mode is enabled
     * @param args The info message(s) and any additional parameters
     * @return this
    */
    logInfo(...args) {
        const main = this.getMain();
        if (main && main.getConfig().app.console.info) {
            const stack = new Error().stack || '';
            const stackLines = stack.split('\n');
            // stackLines[1] is this debug method, stackLines[2] is the actual caller
            const callerInfo = stackLines[2]?.trim() || 'unknown';
            console.info('%c[INFO]', 'color: lightblue;', `${this.toString()}: ${callerInfo}`, ...args);
        }
        return this;
    }
    /** Logs a warn message to the console if debug mode is enabled
     * @param args The warn message(s) and any additional parameters
     * @return this
    */
    logWarn(...args) {
        const main = this.getMain();
        if (main && main.getConfig().app.console.warn) {
            const stack = new Error().stack || '';
            const stackLines = stack.split('\n');
            // stackLines[1] is this debug method, stackLines[2] is the actual caller
            const callerInfo = stackLines[2]?.trim() || 'unknown';
            console.warn('%c[WARN]', 'color: yellow;', `${this.toString()}: ${callerInfo}`, ...args);
        }
        return this;
    }
    /** Logs a debug message to the console if debug mode is enabled
     * @param args The debug message(s) and any additional parameters
     * @return this
    */
    logError(...args) {
        const main = this.getMain();
        if (main && main.getConfig().app.console.error) {
            const stack = new Error().stack || '';
            const stackLines = stack.split('\n');
            // stackLines[1] is this debug method, stackLines[2] is the actual caller
            const callerInfo = stackLines[2]?.trim() || 'unknown';
            console.error('%c[ERROR]', 'color: red;', `${this.toString()}: ${callerInfo}`, ...args);
        }
        return this;
    }
    /** Adds the given Id to the element */
    setId(id) {
        if (typeof id !== 'undefined' && id !== null) {
            this.id = id;
            this.el.setAttribute('id', id);
        }
        return this;
    }
    /** Sets the title attribute of this element */
    setTitle(title) {
        if (this.el instanceof HTMLElement) {
            this.el.title = title;
        }
        if (this.attributes) {
            this.attributes.title = title;
        }
        return this;
    }
    /** Adds the given class name to the element's list of classes
        @param className the class to be appended
        @see https://stackoverflow.com/a/9229821/722785
    */
    setClass(className) {
        if (className !== 'undefined') {
            this.el.setAttribute('class', className);
            if (this.attributes) {
                this.attributes.class = className;
            }
        }
        return this;
    }
    /** Promises to add an array of classnames to this element
        @param classNames An array of class names
    */
    addClasses(classNames) {
        classNames.forEach((className) => this.addClass(className));
        return this;
    }
    /** Dispatches the given Event to this element's siblings
        @param event Event to dispatch
        @param className Event signal classname
    */
    dispatchToSiblings(event, className) {
        if (this.node instanceof EL) {
            this.node.get().filter((c) => c.hasClass(className) && c !== this).forEach((s) => s.do(event));
        }
    }
    /** Used to recursively verify if the reflected Prototype class of the given node
        matches a specified value.  This can be helpful in cases where you need to
        identify the root super class, not just the current one
        @param node Self or reflection of self
        @param className Class name to match
        @param attempt Recursion loop attempt
        @param limit Recursion limit
        @returns True if classname found in proto chain
        @throws {RecursionLimitError} A Recursion error is thrown if limits are exceeded
    */
    checkReflectionPrototype(node, className, attempt = 0, limit = 10) {
        if (attempt < limit) {
            let reflection = Reflect.getPrototypeOf(node); // Can this value be cached?
            if (reflection !== null) {
                if (reflection.constructor.name === 'EL') { // No sense in going any further than EL
                    return false;
                }
                if (reflection.constructor.name === className) {
                    return true;
                }
                return this.checkReflectionPrototype(reflection, className, attempt + 1);
            }
            return false;
        }
        throw new RecursionLimitError(this.className + '.checkReflectionPrototype(): Exceeded attempt limit. (Attempt ' + attempt + ')');
    }
    /** Retrieves the index value for this element among its siblings
        @returns {number} index value
    */
    getIndex() {
        if (this.node instanceof EL) {
            return this.node.get().indexOf(this);
        }
        return 0;
    }
    // /** Checks for the existence of a nested property value
    //     @see https://stackoverflow.com/a/2631198/722785
    //     @param {any} obj Node
    //     @param {any} level Property level
    //     @param {any} rest The rest
    //     @returns {boolean} True if property exists
    // */
    // getNestedProperty(obj: any, level: any, ...rest: any[]): boolean {
    // 	console.log(this.toString() + '.checkNested()', level, obj, rest.length);
    // 	if (typeof obj === 'undefined') {
    // 		return false;
    // 	}
    // 	if (rest.length === 0 && Reflect.apply(hasOwnProperty, obj, level)) {
    // 		return obj[level];
    // 	}
    // 	return this.getNestedProperty(obj[level], ...rest);
    // 	//var hasGivenProperty = Reflect.hasOwnProperty(this, level);
    // 	//var hasGivenProperty = Reflect.apply(hasOwnProperty, this, level);
    // 	//return rest.length === 0 && hasGivenProperty ? true : this.checkNested(this[level], ...rest);
    // }
    /** Retrieve an {@link EL} based on its __proto__
        @description Recursively iterates through parent nodes until an object with the given prototype is found
        @param className The value to search for within this key
        @param node Entry point to traversing the chain
        @param attempt Recursion loop
        @param limit Recursion limit
        @returns Parent Node
        @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/isPrototypeOf
        @throws Will throw an error if recursion attempt exceeds limit
    */
    getProtoTypeByClass(className, node = this.node, attempt = 0, limit = 500) {
        if (node instanceof HTMLBodyElement || node instanceof HTMLHtmlElement) {
            console.log('Node is document.body');
            return null; // null You have reached the top of the chain
        }
        try {
            if (attempt < limit) {
                if (this.checkReflectionPrototype(node, className)) {
                    return node;
                }
                if (node instanceof EL) {
                    return this.getProtoTypeByClass(className, node.node, attempt + 1);
                }
                else {
                    return node;
                }
            }
            throw new RecursionLimitError(this.className + '.getProtoTypeByClass(): Exceeded attempt limit. (Attempt ' + attempt + ')');
        }
        catch (e) {
            if (e instanceof TypeError) {
                /** Possible TypeError(s) include:
                    TypeError: this.getProtoTypeByClass is not a function
                    TypeError: Reflect.getPrototypeOf called on non-object
                    TypeError: Cannot read property '__proto__' of undefined
                */
                // console.warn('A TypeError was caught while attempting to find proto', className, e);
                return null;
            }
            else {
                console.warn('An unknown error occurred');
                throw e;
            }
        }
    }
    /** Catches DOM Exception when event is already being dispatched
        @param element Element Class to dispatch event
        @param event Event
    */
    filterEventDomException(element, event) {
        try {
            Promise.resolve(element.do(event));
        }
        catch (e) {
            if (!(e instanceof DOMException)) { // DOMException: Event is already being dispatched
                console.warn(e instanceof Error ? e.message : e);
                throw e;
            }
        }
    }
    /** Get child element(s) by Name and optionally by Class
        @param name Element Name
        @param className Element Class
        @returns Child Item/Element Filtered Results
        @description This might also be recognized as this.getChildren()
    */
    get(name, className) {
        if (typeof name === 'undefined' && typeof className === 'undefined') {
            return this.children;
        }
        return this.get().filter((c) => (c.name === name
            || (c.attributes && c.attributes.name === name)
            || name === null) && (c.className === className
            || typeof className === 'undefined'));
    }
    /** Retrieves MODEL.ATTRIBUTES.class */
    getClass() {
        return this.className;
    }
    /** Returns the MAIN container
        @returns {Promise<MAIN>} MAIN class
    */
    getMain() {
        try {
            if (typeof this.main === 'undefined') {
                return this.setMain();
            }
            return this.main;
        }
        catch (e) {
            console.warn(e);
            throw new MissingContainerError(this.className + ' is unable to find a MAIN Container', e);
        }
    }
    /** Retrieves the last child in linked list / children
        @returns Tail Element/Model
    */
    getTail() {
        return this.get()[this.get().length - 1];
    }
    /** Retrieves the token value from the DOM Meta tags
        @returns A request verification token
    */
    getToken() {
        const metas = document.getElementsByTagName('meta');
        for (let i = 0; i < metas.length; i++) {
            if (metas[i].getAttribute('name') === 'token') {
                return metas[i].content || '';
            }
        }
        return '';
    }
    /** Retrieves the previous element (if exists)
        @returns Previous Sibling Element
    */
    getPrev() {
        if (this.node instanceof EL) {
            try {
                return this.node.get()[this.node.get().indexOf(this) - 1];
            }
            catch (e) {
                console.warn('Unable to retrieve previous element/sibling', this, e);
                return null;
            }
        }
        return null;
    }
    /** Retrieves the next element (if exists)
        @returns Next Sibling Element
    */
    getNext() {
        if (this.node instanceof EL) {
            try {
                return this.node.get()[this.node.get().indexOf(this) + 1];
            }
            catch (e) {
                console.warn('Unable to retrieve next element/sibling', this, e);
                return null;
            }
        }
        else {
            return null;
        }
    }
    getCallers() {
        return this.callers;
    }
    /** Inserts this node's element' after target node's element'
        @param node Target Node
    */
    insertAfter(node) {
        if (node.el.parentNode) {
            node.el.parentNode.insertBefore(this.el, node.el.nextSibling);
        }
        else {
            console.warn('insertAfter: node.el.parentNode is null', node);
        }
        return this;
    }
    /** Inserts this node's element' before target node's element'
        @param node Target Node
    */
    insertBefore(node) {
        if (node.el.parentNode) {
            node.el.parentNode.insertBefore(this.el, node.el);
        }
        else {
            console.warn('insertBefore: node.el.parentNode is null', node);
        }
        return this;
    }
    /** Inserts this node's element into target node's element
        @param node Target Node
    */
    insertInto(node) {
        if (node instanceof EL) {
            node.el.appendChild(this.el);
        }
        else {
            node.appendChild(this.el);
        }
        return this;
    }
    /** Combines the given model with this model, overriding initial values with given ones */
    merge(model) {
        if (!model) {
            console.log(`${this.className} Model not defined`);
            return; // Prevents error if model is undefined
        }
        // if (model.element === 'svg') {
        // 	console.log('SVG.merge()', model);
        // }
        for (let prop in model) {
            if (typeof prop === 'string') {
                switch (prop) {
                    case 'attributes':
                        this.processAttributes(model.attributes || {});
                        break;
                    // case 'innerHTML':
                    // 	this.setInnerHTML(model[prop]);
                    // 	break;
                    // case 'id':
                    // case 'name':
                    // 	this.set(prop, model[prop]);
                    // 	this.setAttribute(prop, model[prop]);
                    // 	break;
                    case 'children':
                        /** @description model.children are processed during this.make() */
                        //this.build(model);
                        // You can't use the build() method here because the object
                        // hasn't yet inheritted all of its constructors.
                        break;
                    default:
                        this.set(prop, model[prop]);
                }
            }
        }
    }
    /** Adds an Event Listener to this node's element
        @param eventName Event Name
        @param fn Callback on Event
    */
    on(eventName, fn, options) {
        this.el.addEventListener(eventName, fn, options);
        return this;
    }
    /** Dispatches the given event to this node's element
        @param event Event
        @param stopPropagation Optionally stop propagation
    */
    do(event, stopPropagation = false) {
        this.el.dispatchEvent(event);
        if (stopPropagation) {
            event.stopPropagation();
        }
        return this;
    }
    // /** Triggers a Construct Event for the given Element
    // 	@see Constructable
    // 	@param element HTML Element Tag
    // 	@param model Model
    // */
    // construct(element: HTMLElement, model: HTMLElementModel): this {
    // 	return this.do(new Construct(this, element, model), true).getTail();
    // }
    /** Iterates through attributes and sets accordingly
        If attribute is 'innerHTML', the element's innerHTML is modified
    */
    processAttributes(attributes) {
        for (let attr in attributes) {
            if (attr !== 'innerHTML') {
                let value = attributes[attr];
                if (attr === 'class') {
                    this.addClass(value);
                }
                else if (value !== null && typeof value !== 'object' && typeof value !== 'undefined') {
                    this.setAttribute(attr, value);
                }
            }
            else if (attr === 'innerHTML') {
                console.log(this.toString() + '.processAttributes()', attr, attributes[attr]);
                this.setInnerHTML(attributes[attr]);
            }
        }
    }
    /** Removes all child Elements of this Element from the DOM (Preserves this.children)
        @param preserveChildren If true (default), children are preserved
    */
    empty(preserveChildren = true) {
        while (this.el.firstChild) {
            this.el.removeChild(this.el.firstChild);
        }
        if (!preserveChildren) {
            this.children.length = 0;
        }
        return this;
    }
    /** Removes this HTMLElement from the DOM (MODEL is maintained within node.children) */
    remove() {
        if (this.el.parentNode) {
            this.el.parentNode.removeChild(this.el);
        }
        return this;
    }
    /** Removes this Element from the DOM and its Class from any linked lists
        @param delay Millisecond delay
        @param verbose If true, logs to console
        @returns Callback on successful destroy()
    */
    destroy(delay = 0, verbose = false) {
        if (verbose) {
            console.log('Destroying element', this);
        }
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                try {
                    this.remove();
                    if (this.node instanceof EL) {
                        this.node.get().splice(this.node.get().indexOf(this), 1);
                    }
                    resolve(true);
                }
                catch (e) {
                    if (e instanceof TypeError) {
                        if (verbose) {
                            console.warn('Unable to destroy ' + this.toString(), this);
                        }
                        resolve(false);
                    }
                    else {
                        reject(e);
                    }
                }
            }, delay);
        });
    }
    // /** Sets the given attribute to the element and its model */
    setAttribute(key, value) {
        if (!this.attributes) {
            this.attributes = {};
        }
        this.attributes[key] = value;
        this.el.setAttribute(String(key), String(value));
        return this;
    }
    // setAttribute(key: string, value: AttributeValue): this {
    // 	if (typeof key === 'string' && typeof value !== 'undefined' && value !== null) {
    // 		if (this.el instanceof HTMLElement && typeof value === 'string') {
    // 			this.el.setAttribute(key, value);
    // 		}
    // 		try {
    // 			this.attributes.set(key, value);
    // 		} catch (e) {
    // 			if (e instanceof TypeError) {
    // 				this.attributes = new ATTRIBUTES().set(key, value);
    // 			}
    // 		}
    // 	}
    // 	return this;
    // }
    /** Removes the given attribute from the element and sets its model value to null */
    removeAttribute(key) {
        if (typeof key === 'string') {
            try {
                if (this.attributes) {
                    delete this.attributes[key];
                }
                this.el.removeAttribute(key);
            }
            catch (e) {
                console.warn(this.toString() + '.removeAttribute(' + key + '): Unable to remove attribute', e);
            }
        }
        return this;
    }
    /** Gets the main (if exists) and sets it */
    setMain() {
        let main = this.getProtoTypeByClass('MAIN');
        this.main = main;
        return this.main;
    }
    /** Removes the given class name from the element's list of classes */
    removeClass(className) {
        try {
            this.el.classList.remove(className);
            const currentClass = typeof this.attributes?.class === 'string' ? this.attributes.class : '';
            const updatedClass = currentClass.split(' ').filter((v) => v !== className).join(' ');
            if (this.attributes) {
                this.attributes.class = updatedClass;
            }
            return this;
        }
        catch (e) {
            if (e instanceof TypeError) {
                return this;
            }
            console.warn(e);
            return this;
        }
    }
    /** Binds methods of the given interface to this class
        @param iface The interface to implement on this class
    */
    implement(iface) {
        Object.keys(iface.methods).forEach((key) => {
            this[key] = iface.methods[key];
        });
        return this;
    }
    /** Handles a Payload
        @param payload Payload
    */
    handlePayload(payload) {
        //return this.chain(() => console.log('Triggered ' + this.className + '.handle()', payload));
        console.log('Triggered ' + this.className + '.handlePayload()', payload);
    }
    /** Returns true if class exists on this element
        @param className the class to be appended
    */
    hasClass(className) {
        return this.el.classList.contains(className);
    }
    /** Sets the innerHTML of this element */
    setInnerHTML(innerHTML = '') {
        this.el.innerHTML = innerHTML ?? '';
        return this;
    }
    /** Gets the inner HTML of this element */
    getInnerHTML() {
        return this.el.innerHTML;
    }
    /** Scrolls to the top of this element */
    scrollTo(options = {
        behavior: 'smooth',
        block: 'center',
        inline: 'start'
    }) {
        //console.log('Scrolling to ' + this.toString(), this, behavior);
        this.el.scrollIntoView(options);
        return this;
    }
    /** Toggles the given attribute on this element
        @param attribute The attribute to toggle on this element
        @param value The value to assign to the attribute if it is being enabled
    */
    toggleAttribute(attribute, value) {
        if (this.el.hasAttribute(attribute)) {
            this.el.removeAttribute(attribute);
        }
        else {
            this.el.setAttribute(attribute, value);
        }
        return this;
    }
    /** Toggles the given class on this element */
    toggleClass(className = 'active') {
        this.el.classList.toggle(className);
        return this;
    }
    /** Returns a string representation of this Element */
    toString() {
        return this.className + '()';
    }
}
export { AbstractMethodError, HTMLELEMENT, MissingContainerError, PAYLOAD, RecursionLimitError };
